import { useCallback } from "react";
import stringify from "fast-json-stable-stringify";

import { duration } from "@formant/core/model/duration";
import { IDeviceQuery } from "@formant/core/model/IDeviceQuery";
import { IEventQuery } from "@formant/core/model/IEventQuery";

import { deviceDetailsStore, deviceStore, eventStore } from "../../stores";
import { useTimer } from "../common/Timer";

interface IUseStorePollerHook<TArgs extends any[]> {
    (...query: TArgs): void;
}

function createUseStorePoller<TArgs extends any[]>(
    refresher: (...query: TArgs) => Promise<void>,
    defaultInterval: number,
): IUseStorePollerHook<TArgs> {
    return function useStorePoller(...query: TArgs) {
        const updater = useCallback(async () => {
            await refresher(...query);
        }, [stringify(query)]);

        useTimer(updater, {
            interval: defaultInterval,
            wait: true,
        });
    };
}

interface IDeviceQueryProps {
    query: IDeviceQuery;
}

export const useDeviceDetailStorePoller = createUseStorePoller(
    (query: IDeviceQuery | undefined) => deviceDetailsStore.refreshList(query),
    10 * duration.second,
);

export function DeviceDetailsStorePoller(props: IDeviceQueryProps) {
    const { query } = props;
    useDeviceDetailStorePoller(query);
    return null;
}

export const useDevicePoller = createUseStorePoller(
    async (query: IDeviceQuery | undefined) => {
        await deviceStore.refreshList(query);
    },
    10 * duration.second,
);

export function DeviceStorePoller(props: IDeviceQueryProps) {
    const { query } = props;
    useDevicePoller(query);
    return null;
}

export const useEventStorePoller = createUseStorePoller(
    (query: IEventQuery) => eventStore.refreshList(query),
    10 * duration.second,
);

export function EventStorePoller(props: { query: IEventQuery }) {
    const { query } = props;

    useEventStorePoller(query);
    return null;
}
